import React from "react";
import FractionInput from "../../../../components/inputs/FractionInput";
import { BlockMath, InlineMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

// Zadanie: na podstawie liczby boków wielokąta w podstawie (N) oblicz:
// - liczbę krawędzi, albo
// - liczbę ścian, albo
// - liczbę wierzchołków

export function generateCountElementsTask() {
  const N = Math.floor(Math.random() * 5) + 3; // 3–7 boków
  const types = ["edges", "faces", "vertices"];
  const selected = types[Math.floor(Math.random() * types.length)];

  const baseNames = {
    3: "trójkąta",
    4: "czworokąta",
    5: "pięciokąta",
    6: "sześciokąta",
    7: "siedmiokąta",
  };

  const baseName = baseNames[N] || `${N}-kąta`;

  let question = `Dany jest graniastosłup o podstawie ${baseName}. Oblicz liczbę `;
  if (selected === "edges") question += "krawędzi.";
  if (selected === "faces") question += "ścian.";
  if (selected === "vertices") question += "wierzchołków.";

  // rdzeń danych zadania
  const core = { N, selected, question };

  // Zwracamy zarówno „płaski” obiekt (dla starych widoków zadań),
  // jak i pole data (dla UniversalQuizRunnera / quizów).
  return {
    type: "CountElementsTask",
    ...core,
    data: core,
  };
}

// Alias używany przez silnik quizu – inne zadania eksportują generateTask.
export function generateTask() {
  return generateCountElementsTask();
}

// Pomocniczo normalizujemy wejście – w zależności od miejsca wywołania
// może to być albo cały task, albo obiekt w stylu { data: {...} }.
function unwrapTask(taskOrWrapper) {
  if (!taskOrWrapper) return taskOrWrapper;
  return taskOrWrapper.data ? taskOrWrapper.data : taskOrWrapper;
}

export function renderTask(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);

  // <br /> powoduje, że przycisk „Pokaż odpowiedź” przechodzi do nowej linii
  return (
    <p>
      {task.question}
      <br />
    </p>
  );
}

export function renderInput(taskOrWrapper, userAnswer, onChange, disabled) {
  const task = unwrapTask(taskOrWrapper);

  // wybór symbolu zmiennej do podglądu i etykiety
  let label = "k"; // domyślnie: krawędzie
  if (task.selected === "faces") label = "s";      // ściany
  if (task.selected === "vertices") label = "w";   // wierzchołki

  return (
    <div className="flex items-center gap-2">
      <InlineMath math={`${label} =`} />
      <FractionInput
        value={userAnswer || ""}          // przy braku odpowiedzi przekazujemy pusty string
        onChange={(val) => onChange(val)} // zapisujemy dokładnie to, co zwraca FractionInput
        disabled={disabled}
        label={label}                     // TO steruje napisem w podglądzie: k / s / w
      />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  // jeśli brak odpowiedzi, pokaż komunikat zamiast ładować KaTeX
  if (!userAnswer) {
    return <i>(brak odpowiedzi)</i>;
  }

  // FractionInput zwykle zwraca obiekt { raw, parsed }
  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer.raw ?? userAnswer.parsed ?? "";

  if (!raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  // KaTeX dostaje już zwykły string
  return <InlineMath math={String(raw)} />;
}

export function checkAnswer(taskOrWrapper, userAnswer) {
  const task = unwrapTask(taskOrWrapper);

  if (!userAnswer || typeof userAnswer !== "object") return false;
  const given = userAnswer.parsed;
  if (given === null || given === undefined) return false;

  const N = task.N;

  let correct;
  if (task.selected === "edges") correct = 3 * N;
  if (task.selected === "faces") correct = N + 2;
  if (task.selected === "vertices") correct = 2 * N;

  return Math.abs(given - correct) < 0.01;
}

export function renderAnswer(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  const { N, selected } = task;

  if (selected === "edges") {
    // k – liczba krawędzi
    return <InlineMath math={`k = ${3 * N}`} />;
  }

  if (selected === "faces") {
    // s – liczba ścian
    return <InlineMath math={`s = ${N + 2}`} />;
  }

  if (selected === "vertices") {
    // w – liczba wierzchołków
    return <InlineMath math={`w = ${2 * N}`} />;
  }

  return null;
}

export function renderExplanation(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const { N, selected } = task;
  if (N == null || selected == null) return null;

  if (selected === "edges") {
    return (
      <>
        <p>
          <strong>Krok 1:</strong> Zapisujemy wzór na liczbę krawędzi{" "}
          <InlineMath math="k" /> graniastosłupa o podstawie n-kąta:
        </p>
        <BlockMath math="k = 3n" />

        <p>
          <strong>Krok 2:</strong> Podstawiamy{" "}
          <InlineMath math={`n = ${N}`} /> do wzoru.
        </p>
        <BlockMath math={`k = 3 \\cdot ${N} = ${3 * N}`} />
      </>
    );
  }

  if (selected === "faces") {
    return (
      <>
        <p>
          <strong>Krok 1:</strong> Zapisujemy wzór na liczbę ścian{" "}
          <InlineMath math="s" /> graniastosłupa o podstawie n-kąta:
        </p>
        <BlockMath math="s = n + 2" />

        <p>
          <strong>Krok 2:</strong> Podstawiamy{" "}
          <InlineMath math={`n = ${N}`} /> do wzoru.
        </p>
        <BlockMath math={`s = ${N} + 2 = ${N + 2}`} />
      </>
    );
  }

  if (selected === "vertices") {
    return (
      <>
        <p>
          <strong>Krok 1:</strong> Zapisujemy wzór na liczbę wierzchołków{" "}
          <InlineMath math="w" /> graniastosłupa:
        </p>
        <BlockMath math="w = 2n" />

        <p>
          <strong>Krok 2:</strong> Podstawiamy{" "}
          <InlineMath math={`n = ${N}`} /> do wzoru.
        </p>
        <BlockMath math={`w = 2 \\cdot ${N} = ${2 * N}`} />
      </>
    );
  }

  return null;
}
